/* eslint-disable @atlassian/relay/unused-fields */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { graphql, type PreloadedQuery, useFragment, usePreloadedQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import { DEFAULT_BRANDING_COLOR } from '../../constants';
import { HubThemingProvider } from '../../controllers/hub-theming-context';
import {
	NavigationStyleContext,
	type NavigationStyleContextType,
} from '../../controllers/navigation-style-context';
import { useIsHubCornerRadiusEnabled } from '../../controllers/use-is-hub-corner-radius-enabled';
import type { HubAppProps } from '../../types';
import { HubContext } from '../../utils';
import type { HubTypography } from '../../utils/hub-typography';
import { toFontKey } from '../../utils/hub-typography/to-font-key';
import { mapApiToNavigationOptions } from '../../utils/navigation-bar-branding';

import { type hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference$key } from './__generated__/hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference.graphql';
import { type hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference$key } from './__generated__/hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference.graphql';
import { type hubBootstrapWrapperQuery } from './__generated__/hubBootstrapWrapperQuery.graphql';

interface HubBootstrapProps extends HubAppProps {
	bootstrapQueryReference: hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference$key;
	children: React.ReactNode;
	cloudId: string;
	isInPreviewMode?: boolean;
	tenantContextsQueryReference: hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference$key;
}
interface HubBootstrapWrapperProps extends HubAppProps {
	children: React.ReactNode;
	cloudId: string;
	isInPreviewMode?: boolean;
	queryReference: PreloadedQuery<hubBootstrapWrapperQuery>;
}

type BootstrapPageMediaConfigInput =
	| {
			asapIssuer?: string | null;
			mediaCollectionName?: string | null;
			mediaToken?: string | null;
			mediaUrl?: string | null;
	  }
	| null
	| undefined;

/**
 * Narrows the page-scoped media config returned by the bootstrap fragment
 * (where every field is nullable due to `@skip(if: $skipPageMediaConfig)`) to
 * the non-nullable shape expected by `HubContext.pageMediaConfig`. Returns
 * `undefined` when any required field is missing so the caller can omit the
 * key from the context value entirely.
 */
const getPageMediaConfigFromBootstrap = (input: BootstrapPageMediaConfigInput) => {
	if (!input) {
		return undefined;
	}
	const { asapIssuer, mediaCollectionName, mediaToken, mediaUrl } = input;
	if (asapIssuer == null || mediaCollectionName == null || mediaToken == null || mediaUrl == null) {
		return undefined;
	}
	return { asapIssuer, mediaCollectionName, mediaToken, mediaUrl };
};

export const HubBootstrap = ({
	bootstrapQueryReference,
	tenantContextsQueryReference,
	children,
	cloudId,
	isInPreviewMode = false,
	...props
}: HubBootstrapProps): React.JSX.Element => {
	// `skipPageMediaConfig` defaults to `false` so callers that don't pass it
	// fall back to the original behavior of fetching pageMediaConfig in the
	// bootstrap. The wrapper query passes a value derived from the fix flag.
	const data = useFragment<hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference$key>(
		graphql`
			fragment hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference on HelpCenterQueryApi
			@argumentDefinitions(
				pageAri: { type: "ID", defaultValue: "" }
				skipPageMediaConfig: { type: "Boolean", defaultValue: false }
			) {
				hubMediaConfig(ari: $hubAri, operationType: $operationType) @required(action: THROW) {
					asapIssuer @required(action: THROW)
					mediaCollectionName @required(action: THROW)
					mediaToken @required(action: THROW)
					mediaUrl @required(action: THROW)
				}
				pageMediaConfig: hubMediaConfig(ari: $pageAri, operationType: $operationType)
					@skip(if: $skipPageMediaConfig) {
					asapIssuer
					mediaCollectionName
					mediaToken
					mediaUrl
				}
				helpCenterById(helpCenterAri: $hubAri) @optIn(to: "HelpCenterAggBeta") {
					... on HelpCenter {
						type @optIn(to: "HelpCenterTypeTest")
						helpCenterBranding @optIn(to: ["HelpCenterBrandingTest"]) {
							logo {
								fileId
								url
							}
							colors {
								primary
								secondaryColor
							}

							navigationBar @optIn(to: "HelpCenterNavigationBarTest") {
								backgroundColor
								borderColor
								borderType
								cornerRadius
								padding
								positionType
								textColor
							}

							typography @optIn(to: "HelpCenterHubAdvancedTheming") {
								headingFont
								bodyFont
							}

							cornerRadius @optIn(to: "HelpCenterHubAdvancedTheming")
						}
					}
				}
			}
		`,
		bootstrapQueryReference,
	);

	const tenantData =
		useFragment<hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference$key>(
			graphql`
				fragment hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference on Query {
					tenantContexts(cloudIds: [$cloudId]) @include(if: $hubEditMode) {
						cloudUrl
					}
				}
			`,
			tenantContextsQueryReference,
		);

	const cloudUrl = tenantData.tenantContexts?.[0]?.cloudUrl ?? window.location.origin;
	if (!data.helpCenterById) {
		throw new Error('Help center not found');
	}

	const branding = data.helpCenterById?.helpCenterBranding;
	const logo = branding?.logo;
	const primaryColor = branding?.colors?.primary ?? undefined;
	// BE persists the secondary brand color as `secondaryColor` on
	// HelpCenterBrandingColors. We expose it on the FE context as `accent`
	// to match the existing HubThemingContext / panel terminology (admin
	// label is "Accent color"). The bridge happens here at the data
	// boundary.
	const accentColor = branding?.colors?.secondaryColor ?? undefined;

	// Map the BE typography shape (`{ headingFont, bodyFont }`, FontKey strings
	// or null) to the FE `HubTypography` ({ heading, body }). Either side may be
	// null independently — fall back to Atlassian Sans for the missing one. When
	// neither is set we leave `typography` undefined so HubTypographyScope
	// no-ops and the framework default (Atlassian Sans) applies. This is the
	// single read seam that feeds BOTH the editor and all help-seeker view
	// pages — they share this bootstrap.
	//
	// `toFontKey` validates each value against FONT_REGISTRY rather than blindly
	// casting: an unrecognised backend string (a font added server-side before
	// this enum ships, a migration) would otherwise reach the registry lookups
	// in HubTypographyScope / buildGoogleFontsUrl and throw on `undefined`.
	const beTypography = branding?.typography;
	const headingFont = beTypography?.headingFont ?? undefined;
	const bodyFont = beTypography?.bodyFont ?? undefined;
	// Stable reference keyed on the primitive font strings so the
	// hubContextValue memo below can depend on `typography` directly without
	// recomputing on every render (and exhaustive-deps stays satisfied).
	const typography = useMemo<HubTypography | undefined>(
		() =>
			headingFont || bodyFont
				? {
						heading: toFontKey(headingFont),
						body: toFontKey(bodyFont),
					}
				: undefined,
		[headingFont, bodyFont],
	);

	// Hub-wide corner radius. The BE field is `HelpCenterBranding.cornerRadius`
	// (distinct from `navigationBar.cornerRadius`). `?? undefined` normalises
	// GraphQL null to undefined so absent vs. not-set are indistinguishable to
	// consumers — both fall back to the framework default (8 px).
	const cornerRadius = branding?.cornerRadius ?? undefined;

	const fileId = logo?.fileId || '';
	const url = logo?.url || '';
	const hubType = data.helpCenterById?.type || '';

	// Convert the logo to a base64 data URL so the signed token embedded in the
	// original URL never expires while the user has the page open.
	const [logoUrl, setLogoUrl] = useState(url);
	const prevUrlRef = useRef(url);
	if (prevUrlRef.current !== url) {
		prevUrlRef.current = url;
		setLogoUrl(url);
	}

	useEffect(() => {
		if (!url) {
			return;
		}

		let cancelled = false;
		fetch(url)
			.then((res) => res.blob())
			.then(
				(blob) =>
					new Promise<string>((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = () => resolve(reader.result as string);
						reader.onerror = reject;
						reader.readAsDataURL(blob);
					}),
			)
			.then((base64) => {
				if (!cancelled) {
					setLogoUrl(base64);
				}
			})
			.catch(() => {
				// Silently fall back to the original signed URL if the fetch fails.
			});

		return () => {
			cancelled = true;
		};
	}, [url]);

	const navigationBar = branding?.navigationBar ?? null;

	// When the fix flag is ON, `pageMediaConfig` is omitted from the bootstrap
	// and is instead provided by the page-scoped `Hub` (read mode) and `HubInfo`
	// (edit mode) components via a nested `HubContext.Provider`. When the flag
	// is OFF the bootstrap still fetches it (via `$pageAri`) and provides it
	// here as before.
	const bootstrapPageMediaConfig = fg('hubs_fix_rerendering_bootstrap_page_media')
		? undefined
		: getPageMediaConfigFromBootstrap(data.pageMediaConfig);

	// Memoize the context value so that consumers (e.g. `Hub`) receive a stable
	// reference across re-renders. Without this, every render of `HubBootstrap`
	// produces a new object literal, causing all `useHubContext()` consumers to
	// re-render even when nothing meaningful has changed. This is especially
	// important for the `useMemo` in `Hub` that merges `pageMediaConfig` into
	// the context — its deps array includes `hubContext`, so it needs a stable
	// input to avoid recomputing on every render.
	const hubContextValue = useMemo(
		() => ({
			...(bootstrapPageMediaConfig ? { pageMediaConfig: bootstrapPageMediaConfig } : {}),
			hubMediaConfig: data.hubMediaConfig,
			branding: {
				logo: {
					fileId,
					url: logoUrl,
				},
				colors: {
					primary: primaryColor || DEFAULT_BRANDING_COLOR,
					// `accent` is intentionally NOT defaulted here — the
					// HubThemingProvider owns the FE-side default so a single
					// constant governs both "no BE field set" (returns null)
					// and "outside HubBootstrap entirely" (Confluence/CSM)
					// cases. Passing `undefined` lets the provider's
					// `branding?.colors?.accent ?? DEFAULT_ACCENT` shortcut
					// kick in either way.
					...(accentColor ? { accent: accentColor } : {}),
				},
				// Only present when the BE has a stored choice; undefined falls
				// through to the Atlassian Sans framework default downstream.
				...(typography ? { typography } : {}),
				// Only present when the BE has a stored value; undefined means
				// consumers fall back to the framework default (8 px).
				...(cornerRadius !== undefined ? { cornerRadius } : {}),
			},
			layout: props.layout,
			user: props.user,
			env: props.env,
			actions: props.actions,
			containerStyling: props.containerStyling,
			renderAvatarMenuOptions: props.renderAvatarMenuOptions,
			renderRightNavActions: props.renderRightNavActions,
			featureFlags: props.featureFlags,
			headerless: props.headerless,
			cloudId,
			cloudUrl,
			hubType,
			isInPreviewMode,
		}),
		[
			bootstrapPageMediaConfig,
			data.hubMediaConfig,
			fileId,
			logoUrl,
			primaryColor,
			accentColor,
			typography,
			cornerRadius,
			props.layout,
			props.user,
			props.env,
			props.actions,
			props.containerStyling,
			props.renderAvatarMenuOptions,
			props.renderRightNavActions,
			props.featureFlags,
			props.headerless,
			cloudId,
			cloudUrl,
			hubType,
			isInPreviewMode,
		],
	);

	return (
		<HubContext.Provider value={hubContextValue}>
			<HubThemingProvider>
				<NavigationStyleProvider navigationBar={navigationBar} primaryColor={primaryColor}>
					{children}
				</NavigationStyleProvider>
			</HubThemingProvider>
		</HubContext.Provider>
	);
};

/**
 * Provides the `NavigationStyleContext` derived from the BE navigation-bar
 * branding.
 *
 * IMPORTANT — why this is a separate component:
 * The corner-radius gate (`useIsHubCornerRadiusEnabled` →
 * `useIsAdvancedThemingEnabled`) reads `useContext(HubContext)`. It MUST run
 * *inside* the `<HubContext.Provider>` that `HubBootstrap` renders — otherwise
 * (as when it was inlined in `HubBootstrap` above the provider) it reads the
 * absent outer context, always resolves `false`, and the mapper coerces an
 * unset `navigationBar.cornerRadius` (null) to the 8px default. That pins the
 * navbar and stops it inheriting the hub-wide radius in view mode. Evaluating
 * the gate here keeps it consistent with the page-scoped `Hub` mapper so the
 * navbar correctly inherits (null) until an explicit radius is set.
 */
const NavigationStyleProvider = ({
	navigationBar,
	primaryColor,
	children,
}: {
	children: React.ReactNode;
	navigationBar: Parameters<typeof mapApiToNavigationOptions>[0] | null;
	primaryColor: string | undefined;
}) => {
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const navStyleOptions = useMemo<NavigationStyleContextType>(
		() => ({
			options: navigationBar
				? mapApiToNavigationOptions(navigationBar, primaryColor, isCornerRadiusEnabled)
				: null,
			setOptions: () => void 0,
		}),
		[navigationBar, primaryColor, isCornerRadiusEnabled],
	);

	return (
		<NavigationStyleContext.Provider value={navStyleOptions}>
			{children}
		</NavigationStyleContext.Provider>
	);
};

export const HubBootstrapWrapper = ({
	children,
	queryReference,
	...props
}: HubBootstrapWrapperProps): React.JSX.Element | null => {
	const data = usePreloadedQuery<hubBootstrapWrapperQuery>(
		graphql`
			query hubBootstrapWrapperQuery(
				$cloudId: ID!
				$hubEditMode: Boolean!
				$hubAri: ID!
				$pageAri: ID!
				$operationType: HelpCenterMediaConfigOperationType
				$skipPageMediaConfig: Boolean!
			) {
				helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
					...hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference
						@arguments(pageAri: $pageAri, skipPageMediaConfig: $skipPageMediaConfig)
				}
				...hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference
			}
		`,
		queryReference,
	);

	if (!data.helpCenter) {
		return null;
	}

	return (
		<HubBootstrap
			bootstrapQueryReference={data.helpCenter}
			tenantContextsQueryReference={data}
			{...props}
		>
			{children}
		</HubBootstrap>
	);
};
